import React, { useState } from 'react';
import { Shield, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { useCustomerApi } from '@/contexts/DashboardCustomerContext';
import DetailModal from '../common/DetailModal';

interface ComplianceWidgetProps {
  data: any;
}

const ComplianceWidget: React.FC<ComplianceWidgetProps> = ({ data }) => {
  const apiClient = useCustomerApi();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState<any>(null);
  const [modalType, setModalType] = useState<'risk' | 'category' | 'contract' | 'system'>('risk');
  const [modalTitle, setModalTitle] = useState('');
  const [modalSubtitle, setModalSubtitle] = useState('');

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-600 dark:text-green-400';
    if (score >= 70) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  const getSeverityColor = (severity: string) => {
    switch (severity.toLowerCase()) {
      case 'critical':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      case 'high':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'low':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
      case 'best_practice':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
    }
  };

  const handleRiskClick = async (severity: string, count: number) => {
    // Fetch real risk data from the API
    try {
      const response = await apiClient.get('/dashboard/compliance/risks', {
        params: { severity }
      });
      const riskData = response.data;

      setModalType('risk');
      setModalTitle(`${severity} Risks`);
      setModalSubtitle(`${count} ${severity.toLowerCase()} priority issues detected`);
      setModalData(riskData);
      setModalOpen(true);
    } catch (error) {
      console.error('Failed to fetch risk details:', error);
      // Fallback to basic data if API fails
      setModalType('risk');
      setModalTitle(`${severity} Risks`);
      setModalSubtitle(`${count} ${severity.toLowerCase()} priority issues detected`);
      setModalData({ items: [], total_count: 0 });
      setModalOpen(true);
    }
  };

  const handleCategoryClick = async (category: string, _count: number) => {
    // Fetch real category data from the API
    try {
      const response = await apiClient.get('/dashboard/compliance/risks', {
        params: { category }
      });
      const categoryData = response.data;

      setModalType('category');
      setModalTitle(`${category} Issues`);
      setModalSubtitle(`Detailed breakdown of ${category} related issues`);
      setModalData(categoryData);
      setModalOpen(true);
    } catch (error) {
      console.error('Failed to fetch category details:', error);
      setModalType('category');
      setModalTitle(`${category} Issues`);
      setModalSubtitle(`Detailed breakdown of ${category} related issues`);
      setModalData({ items: [], total_count: 0 });
      setModalOpen(true);
    }
  };

  const handleContractClick = async (status: string, count: number) => {
    if (count === 0) return;

    try {
      const response = await apiClient.get('/dashboard/contracts', {
        params: { status }
      });

      const responseData = response.data;

      setModalType('contract');
      setModalTitle(`${status.charAt(0).toUpperCase() + status.slice(1)} Contracts`);
      setModalSubtitle(`${responseData.total_contracts || 0} contracts covering ${responseData.total_systems || count} systems`);
      setModalData(responseData);
      setModalOpen(true);
    } catch (error) {
      console.error('Failed to fetch contract details:', error);
      setModalType('contract');
      setModalTitle(`${status.charAt(0).toUpperCase() + status.slice(1)} Contracts`);
      setModalSubtitle(`${count} contracts`);
      setModalData({ contracts: [], total_contracts: count, total_systems: 0 });
      setModalOpen(true);
    }
  };

  const overallScore = data?.overall_score || 0;
  const riskSummary = data?.risk_summary || {};
  const recommendations = data?.recommendations || [];

  return (
    <>
      <div className="bg-white dark:bg-slate-800/95 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Security & Compliance
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Overall compliance and risk assessment
            </p>
          </div>
          <div className="text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5, type: "spring" }}
              className={`text-4xl font-bold ${getScoreColor(overallScore)}`}
            >
              {overallScore}%
            </motion.div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Compliance Score
            </p>
          </div>
        </div>

        {/* Risk Summary Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Risk Distribution */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              Risk Distribution
            </h4>
            <div className="space-y-2">
              {['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'BEST_PRACTICE'].map((severity) => {
                const severityKey = severity === 'BEST_PRACTICE' ? 'best_practice' : severity.toLowerCase();
                const count = riskSummary.by_severity?.[severityKey] || 0;
                const displayName = severity === 'BEST_PRACTICE' ? 'BEST PRACTICE' : severity;
                return (
                  <button
                    key={severity}
                    onClick={() => handleRiskClick(severity, count)}
                    className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 cursor-pointer group"
                  >
                    <span className={`text-xs font-medium px-2 py-1 rounded ${getSeverityColor(severity)}`}>
                      {displayName}
                    </span>
                    <span className="font-semibold text-gray-900 dark:text-white group-hover:scale-110 transition-transform">
                      {count}
                    </span>
                  </button>
                );
              })}
              <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => handleRiskClick('ALL', riskSummary.total || 0)}
                  className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200"
                >
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Total Risks
                  </span>
                  <span className="font-bold text-gray-900 dark:text-white hover:scale-110 transition-transform">
                    {riskSummary.total || 0}
                  </span>
                </button>
              </div>
            </div>
          </div>

          {/* Risk Categories */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              Risk Categories
            </h4>
            <div className="space-y-2">
              {Object.entries(riskSummary.by_category || {}).map(([category, count]: [string, any]) => (
                <button
                  key={category}
                  onClick={() => handleCategoryClick(category, count)}
                  className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 cursor-pointer group"
                >
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {category}
                  </span>
                  <span className="font-semibold text-gray-900 dark:text-white group-hover:scale-110 transition-transform">
                    {count}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Contract Status */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              Contract Status
            </h4>
            <div className="space-y-2">
              <button
                onClick={() => handleContractClick('active', data?.contract_summary?.active || 0)}
                className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 cursor-pointer group"
              >
                <span className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Active</span>
                </span>
                <span className="font-semibold text-green-600 dark:text-green-400 group-hover:scale-110 transition-transform">
                  {data?.contract_summary?.active || 0}
                </span>
              </button>
              <button
                onClick={() => handleContractClick('expiring', data?.contract_summary?.expiring || 0)}
                className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 cursor-pointer group"
              >
                <span className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-yellow-500" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Expiring</span>
                </span>
                <span className="font-semibold text-yellow-600 dark:text-yellow-400 group-hover:scale-110 transition-transform">
                  {data?.contract_summary?.expiring || 0}
                </span>
              </button>
              <button
                onClick={() => handleContractClick('expired', data?.contract_summary?.expired || 0)}
                className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 cursor-pointer group"
              >
                <span className="flex items-center gap-2">
                  <XCircle className="w-4 h-4 text-red-500" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Expired</span>
                </span>
                <span className="font-semibold text-red-600 dark:text-red-400 group-hover:scale-110 transition-transform">
                  {data?.contract_summary?.expired || 0}
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Recommendations */}
        {recommendations.length > 0 && (
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              Recommendations
            </h4>
            <div className="space-y-2">
              {recommendations.slice(0, 3).map((rec: string, idx: number) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className="flex items-start gap-2"
                >
                  <Shield className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-600 dark:text-gray-400">{rec}</p>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </div>

      <DetailModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={modalTitle}
        subtitle={modalSubtitle}
        data={modalData}
        type={modalType}
      />
    </>
  );
};

export default ComplianceWidget;